import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Input } from '@/components/form/fields/Input/Input';
import { Checkbox } from '@/components/form/fields/Checkbox/Checkbox';
import { Notification } from '@/components/Notification/Notification';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { WebhookEndpointType } from '@/types/invoiceVaultTypes';
import { createWebhook } from '../../../api/api';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { FieldRow } from '@/components/form/FieldRow/FieldRow';
import { WebhooksEndpoints } from './WebhooksEndpoints';

type PropsType = { endpoints: WebhookEndpointType[]; events: string[]; canEdit: boolean; onChanged: () => void };

export const WebhooksPanel = ({ endpoints, events, canEdit, onChanged }: PropsType) => {
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState<string[]>(events);
  const [error, setError] = useState('');
  const [secretOnce, setSecretOnce] = useState<{ id: string; secret: string } | null>(null);

  const create = useMutation({
    mutationFn: () => createWebhook({ url: url.trim(), events: selected }),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));

      amplitudeService.logEvent(AMPLITUDE_EVENTS.WebhookCreated);
      setError('');
      setUrl('');
      setSecretOnce({ id: response.body.id, secret: response.body.secret });
      onChanged();
    },
  });

  return (
    <>
      <DetailsWidget>
        <Typography variant='subTitle'>Webhooks</Typography>
        <Typography variant='bodySmall'>
          We POST a JSON event to your endpoint when something happens to an invoice link or check. Every request is
          signed: <code>X-WireVault-Signature: sha256=HMAC(secret, body)</code>. Failed deliveries retry five times over
          four hours.
        </Typography>

        {canEdit ? (
          <Box display='flex' flexDirection='column' marginTop='16px' alignItems='flex-start'>
            <Input
              name='webhook_url'
              label={'Add an endpoint (HTTPS URL):'}
              placeholder='https://hooks.example.com/wirevault'
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Box display='flex' gap='20px' flexDirection='column' alignItems='flex-start'>
              <FieldRow columns={[6, 6]} rowGap='12px'>
                {events.map((event) => (
                  <Checkbox
                    key={event}
                    name={`new-${event}`}
                    label={event}
                    checked={selected.includes(event)}
                    onChange={(e) =>
                      setSelected(e.target.checked ? [...selected, event] : selected.filter((x) => x !== event))
                    }
                  />
                ))}
              </FieldRow>

              {error && <Notification variant='error'>{error}</Notification>}
              <ButtonNew
                size='medium'
                disabled={!url.trim() || selected.length === 0}
                isLoading={create.isPending}
                onClick={() => create.mutate()}
              >
                Add endpoint
              </ButtonNew>
            </Box>
          </Box>
        ) : (
          <Notification variant='info'>Only master admins manage webhooks.</Notification>
        )}

        <WebhooksEndpoints
          endpoints={endpoints}
          events={events}
          secretOnce={secretOnce}
          canEdit={canEdit}
          onChanged={onChanged}
          onSetSecret={setSecretOnce}
        />
      </DetailsWidget>
    </>
  );
};
